import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    OnChangeFn,
    PaginationState,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import { DataTablePagination } from "@/components/shared/DataTablePagination";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
    AlertCircle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    FileText,
    SearchX,
} from "lucide-react";

interface DataTableProps<TData, TValue> {
    data: TData[];
    columns: ColumnDef<TData, TValue>[];
    rowCount?: number;
    isLoading?: boolean;
    isError?: boolean;
    hasActiveFilters?: boolean;
    onClearFilters?: () => void;
    onRetry?: () => void;
    pagination: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
    sorting?: SortingState;
    onSortingChange?: OnChangeFn<SortingState>;
}

export function DataTable<TData, TValue>({
    data,
    columns,
    rowCount,
    isLoading,
    isError,
    hasActiveFilters,
    onClearFilters,
    onRetry,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
}: DataTableProps<TData, TValue>) {
    const table = useReactTable({
        data,
        columns,
        rowCount,
        state: {
            pagination,
            ...(sorting ? { sorting } : {}),
        },
        onPaginationChange,
        onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
    });

    const colCount = columns.length;

    return (
        <div>
            <div className="overflow-hidden rounded-md border">
                <Table className="table-fixed">
                    <TableHeader className="bg-muted/50">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const canSort = header.column.getCanSort();
                                    const sorted = header.column.getIsSorted();

                                    return (
                                        <TableHead
                                            key={header.id}
                                            className="text-muted-foreground font-medium"
                                            style={{ width: header.getSize() }}
                                        >
                                            {header.isPlaceholder ? null : canSort ? (
                                                <Button
                                                    variant={"ghost"}
                                                    className={cn(
                                                        "hover:text-foreground -ml-2 flex items-center gap-1 rounded-sm px-2 py-1 transition-colors",
                                                        sorted &&
                                                            "text-foreground"
                                                    )}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                                    {sorted === "asc" ? (
                                                        <ArrowUp className="ml-2 h-4 w-4" />
                                                    ) : sorted === "desc" ? (
                                                        <ArrowDown className="ml-2 h-4 w-4" />
                                                    ) : (
                                                        <ArrowUpDown className="ml-2 h-4 w-4" />
                                                    )}
                                                </Button>
                                            ) : (
                                                flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext()
                                                )
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            Array.from({ length: 8 }).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    {Array.from({ length: colCount }).map(
                                        (_, j) => (
                                            <TableCell
                                                key={`skeleton-${i}-${j}`}
                                            >
                                                <Skeleton
                                                    className={cn(
                                                        "h-4 rounded",
                                                        j === 0
                                                            ? "w-40"
                                                            : "w-20"
                                                    )}
                                                />
                                            </TableCell>
                                        )
                                    )}
                                </TableRow>
                            ))
                        ) : isError ? (
                            <TableRow>
                                <TableCell colSpan={colCount} className="h-48">
                                    <div className="flex flex-col items-center justify-center gap-3 text-center">
                                        <AlertCircle className="text-muted-foreground/50 size-10" />
                                        <div>
                                            <p className="text-sm font-medium">
                                                Failed to load documents
                                            </p>
                                            <p className="text-muted-foreground mt-0.5 text-xs">
                                                Something went wrong. Please try
                                                again.
                                            </p>
                                        </div>
                                        {onRetry && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => onRetry()}
                                            >
                                                Retry
                                            </Button>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : hasActiveFilters ? (
                            <TableRow>
                                <TableCell colSpan={colCount} className="h-48">
                                    <div className="flex flex-col items-center justify-center gap-3 text-center">
                                        <SearchX className="text-muted-foreground/50 size-10" />
                                        <div>
                                            <p className="text-sm font-medium">
                                                No documents match your filters
                                            </p>
                                            <p className="text-muted-foreground mt-0.5 text-xs">
                                                Try adjusting your search or
                                                filters.
                                            </p>
                                        </div>
                                        {onClearFilters && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={onClearFilters}
                                            >
                                                Clear filters
                                            </Button>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <TableRow>
                                <TableCell colSpan={colCount} className="h-48">
                                    <div className="flex flex-col items-center justify-center gap-3 text-center">
                                        <FileText className="text-muted-foreground/50 size-10" />
                                        <div>
                                            <p className="text-sm font-medium">
                                                No documents yet
                                            </p>
                                            <p className="text-muted-foreground mt-0.5 text-xs">
                                                Documents will appear here once
                                                uploaded.
                                            </p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="py-4">
                <DataTablePagination table={table} entityLabel="documents" />
            </div>
        </div>
    );
}
